import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { apiDomain } from 'configs/configs';
import {
  Select,
  Flex,
  Input,
  Form,
  Button,
  Grid,
  Upload,
  Modal,
  UploadFile,
} from 'antd';
import { Modal as MobileModal } from 'antd-mobile';
import { UploadOutlined } from '@ant-design/icons';
import { UploadChangeParam } from 'antd/es/upload/interface';
import { create } from 'zustand';
import {
  StageUnitType,
  ObjectType,
  SurfaceType,
  MeasureGroupType,
  MeasureElementType,
  MeasureType,
} from 'pages/admin/objectsAndClients/objects/types';

const { useBreakpoint } = Grid;

type ComponentProps = {
  stageUnitsList: { isLoaded: boolean; data: StageUnitType[] };
  stagesList: ObjectType['stages'];
  surfacesList: { isLoaded: boolean; data: SurfaceType[] };
  measures: MeasureType[];
  setMeasures: (update: (state: MeasureType[]) => MeasureType[]) => void;
  isAddElementMode: boolean;
  setIsAddElementMode: (isAddElementMode: boolean) => void;
  isEditElementMode: string | undefined;
  setIsEditElementMode: (isEditElementMode?: string) => void;
};

type OperationsStoreState = {
  isFileUploading: boolean;
  setIsFileUploading: (isError: boolean) => void;
  previewFile?: UploadFile;
  setPreviewFile: (previewFile?: UploadFile) => void;
  isSubmittable: boolean;
  setIsSubmittable: (isSubmittable: boolean) => void;
};

const useOperationsStore = create<OperationsStoreState>((set) => ({
  isFileUploading: false,
  setIsFileUploading: (isFileUploading) => set({ isFileUploading }),
  previewFile: undefined,
  setPreviewFile: (previewFile?: UploadFile) => set({ previewFile }),
  isSubmittable: false,
  setIsSubmittable: (isSubmittable) => set({ isSubmittable }),
}));

const Component: React.FC<ComponentProps> = ({
  stageUnitsList,
  stagesList,
  surfacesList,
  measures,
  setMeasures,
  isAddElementMode,
  setIsAddElementMode,
  isEditElementMode,
  setIsEditElementMode,
}) => {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const measuresRef = useRef(measures);
  const valuesRef = useRef(values);

  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const aviableUnits = useMemo(() => {
    const result: StageUnitType[] = [];

    stagesList.forEach((stage) => {
      if (stage.status !== 'active') return;

      stageUnitsList.data.forEach((stageUnit) => {
        stage.units?.forEach((unitInStage) => {
          if (unitInStage.id === stageUnit.id) {
            if (!result.find((resultItem) => resultItem.id === stageUnit.id))
              result.push(stageUnit);
          }
        });
      });
    });

    return result;
  }, [stagesList, stageUnitsList]);

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    measuresRef.current = measures;
  }, [measures]);

  const {
    isFileUploading,
    setIsFileUploading,
    previewFile,
    setPreviewFile,
    isSubmittable,
    setIsSubmittable,
  } = useOperationsStore((store) => store);

  const unitKeysString = JSON.stringify(values?.units);

  //  Triggers when isEditMode changed. Set initial fields value in form or reset it. Reset error.
  useEffect(() => {
    const { group, building, element } = measuresRef.current.reduce<{
      group: MeasureGroupType | undefined;
      building: MeasureType | undefined;
      element: MeasureElementType | undefined;
    }>(
      (result, building) => {
        if (result.element) return result; // Если элемент найден, выходим.

        building.groups?.forEach((group) => {
          const foundElement = group.elements?.find(
            (element) => element.id.toString() === isEditElementMode
          );

          if (foundElement) {
            result.element = foundElement;
            result.group = group;
            result.building = building;
          }
        });

        return result;
      },
      { group: undefined, building: undefined, element: undefined }
    );

    if (!element || !group || !building) form.resetFields();

    if (element && group && building) {
      const file = element.user_file
        ? [
            {
              uid: element.user_file.id,
              name: element.user_file.file_name,
              size: element.user_file.size,
              type: element.user_file.mime_type,
              response: [
                {
                  file_name: element.user_file.file_name,
                  file_path: element.user_file.file_path,
                  mime_type: element.user_file.mime_type,
                  size: element.user_file.size,
                  id: element.user_file.id,
                },
              ],
              thumbUrl: `${apiDomain}/userFiles/${element.user_file.file_path}`,
            },
          ]
        : [];

      const units = element.units
        ? element.units.reduce<{ [id: string]: string | null }>(
            (result, unit) => {
              result[unit.id.toString()] = unit.value
                ? parseFloat(unit.value)
                    .toFixed(2)
                    .toString()
                : null;

              return result;
            },
            {}
          )
        : {};

      form.setFieldsValue({
        building: building.id ? building.id.toString() : undefined,
        group: group.id ? group.id.toString() : undefined,
        name: element.name,
        file: file,
        surface_id: element.surface_id ? element.surface_id : undefined,
        comment: element.comment,
        units: units,
      });
    }

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isEditElementMode, form, measuresRef, valuesRef, setIsSubmittable]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [
    form,
    values?.building,
    values?.group,
    values?.name,
    values?.file,
    values?.surface_id,
    values?.comment,
    unitKeysString,
    setIsSubmittable,
  ]);

  const groupsList = useMemo(() => {
    const building = values?.building ? values.building.toString() : undefined;

    if (!building) return [];

    const measure = measures.find(
      (measure) => measure.id.toString() === building
    );

    if (!measure) return [];

    if (!measure?.groups) return [];

    return measure.groups;
  }, [measures, values?.building]);

  //    Building changed
  useEffect(() => {
    const allValues = form.getFieldsValue();

    const groupId = allValues?.group
      ? allValues?.group
      : groupsList.length
      ? groupsList[0]?.id.toString()
      : '';

    form.setFieldsValue({
      group: groupId,
    });

    if (allValues?.name) return;

    const currentGroup = groupId
      ? measuresRef.current
          .flatMap((measure) => measure.groups || [])
          .find((group) => group.id.toString() === groupId)
      : undefined;

    const maxNumber = currentGroup?.elements
      ? currentGroup.elements
          .map((element) => element.name)
          .filter((name) => /^\d/.test(name))
          .map((name) => parseInt(name.match(/^\d+/)?.[0] || '0', 10))
          .reduce((max, current) => Math.max(max, current), 0)
      : 0;

    const name = (maxNumber + 1).toString();

    form.setFieldsValue({
      name: name,
    });
  }, [groupsList, form, measuresRef, values?.building]);

  //    Units changed
  useEffect(() => {
    const allValues = form.getFieldsValue();

    const units = allValues?.units as { [key: string]: string } | undefined;

    if (!units) return;

    form.setFieldsValue({
      units: Object.keys(units).reduce(
        (result: { [key: string]: string }, unitKey) => {
          result[unitKey] = !units[unitKey]
            ? ''
            : units[unitKey]
                .replace(/[^\d.,]/g, '')
                .replace(/,/g, '.')
                .replace(/^(\d*(\.\d{0,2})?).*$/, '$1');

          return result;
        },
        {}
      ),
    });
  }, [form, unitKeysString]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    try {
      const building: string = values.building ? values.building : '';
      const group: string = values.group ? values.group : '';
      const name = values.name ? values.name.trim() : '';
      const surface_id = values.surface_id ? values.surface_id : '';
      const comment = values.comment ? values.comment.trim() : '';
      const fileId = values?.file?.[0]?.response?.[0]?.id
        ? values?.file?.[0]?.response[0].id
        : values?.file?.[0]?.uid && !isNaN(Number(values?.file?.[0].uid))
        ? Number(values?.file?.[0].uid)
        : null;

      const valuesUnits = values.units as { [key: string]: string } | undefined;

      const unitsValues: { [key: string]: string } = valuesUnits
        ? Object.keys(valuesUnits).reduce(
            (result: { [key: string]: string }, unitKey) => {
              result[unitKey] = valuesUnits[unitKey]
                .replace(/[^\d.,]/g, '')
                .replace(/,/g, '.')
                .replace(/^(\d*(\.\d{0,2})?).*$/, '$1');

              return result;
            },
            {}
          )
        : {};

      const units: MeasureElementType['units'] = stageUnitsList.data
        .filter((stageUnit) => {
          return !!stagesList.find((stage) => {
            if (stage.status !== 'active') return false;

            return stage.units
              ? !!stage.units.find((unitInStage) => {
                  if (unitInStage.id !== stageUnit.id) return false;

                  return true;
                })
              : false;
          });
        })
        .map((stageUnit) => {
          const valueKey = Object.keys(unitsValues).find(
            (unitValue) => unitValue.toString() === stageUnit.id.toString()
          );

          const value =
            valueKey && unitsValues[valueKey] ? unitsValues[valueKey] : null;

          return { ...stageUnit, value: value, status: undefined };
        });

      const userFile = fileId
        ? {
            id: fileId,
            file_name: values?.file?.[0]?.response?.[0]?.file_name
              ? values?.file?.[0]?.response[0].file_name
              : values?.file?.[0]?.name
              ? values?.file?.[0].name
              : null,
            file_path: values?.file?.[0]?.response?.[0]?.file_path
              ? values?.file?.[0]?.response[0].file_path
              : null,
            mime_type: values?.file?.[0]?.response?.[0]?.mime_type
              ? values?.file?.[0]?.response[0].mime_type
              : values?.file?.[0]?.type
              ? values?.file?.[0].type
              : null,
            size: values?.file?.[0]?.response?.[0]?.size
              ? values?.file?.[0]?.response[0].size
              : values?.file?.[0]?.size
              ? values?.file?.[0].size
              : null,
          }
        : undefined;

      setMeasures((measures) => {
        if (isEditElementMode) {
          // Remove from not current groups
          return measures.map((measure) => {
            if (measure.id.toString() !== building) {
              return {
                ...measure,
                groups: measure.groups
                  ? measure.groups.map((groupItem) => {
                      return {
                        ...groupItem,
                        elements: groupItem.elements
                          ? groupItem.elements.filter(
                              (element) =>
                                element.id.toString() !== isEditElementMode
                            )
                          : [],
                      };
                    })
                  : [],
              };
            }

            return {
              ...measure,
              groups: measure.groups
                ? measure.groups.map((groupItem) => {
                    //  Remove from not current groups
                    if (groupItem.id.toString() !== group) {
                      return {
                        ...groupItem,
                        elements: groupItem.elements
                          ? groupItem.elements.filter(
                              (element) =>
                                element.id.toString() !== isEditElementMode
                            )
                          : [],
                      };
                    }

                    const elements = groupItem?.elements?.length
                      ? groupItem.elements
                      : [];

                    return {
                      ...groupItem,
                      elements: elements.find(
                        (element) => element.id.toString() === isEditElementMode
                      )
                        ? elements.map((elementItem) => {
                            if (elementItem.id.toString() === isEditElementMode)
                              return {
                                ...elementItem,
                                name: name,
                                user_file_id: fileId,
                                user_file: userFile,
                                surface_id: surface_id,
                                comment: comment,
                                units: units,
                              };

                            return elementItem;
                          })
                        : [
                            ...elements,
                            {
                              id: isEditElementMode,
                              name: name,
                              user_file_id: fileId,
                              user_file: userFile,
                              surface_id: surface_id,
                              comment: comment,
                              units: units,
                            },
                          ],
                    };
                  })
                : [],
            };
          });
        }

        const maxId = measures
          .flatMap(
            (measure) =>
              measure.groups?.flatMap(
                (groupItem) => groupItem.elements || []
              ) || []
          )
          .filter((element) => element.id.toString()?.startsWith('newElement-'))
          .map((element) => parseInt(element.id.toString().split('-')[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        const newId = `newElement-${maxId + 1}`;

        return measures.map((measure) => {
          if (measure.id.toString() === building)
            return {
              ...measure,
              groups: measure.groups
                ? measure.groups.map((groupItem) => {
                    if (groupItem.id.toString() === group)
                      return {
                        ...groupItem,
                        elements: [
                          ...(groupItem.elements ? groupItem.elements : []),
                          {
                            id: newId,
                            name: name,
                            user_file_id: fileId,
                            user_file: userFile,
                            surface_id: surface_id,
                            comment: comment,
                            units: units,
                          },
                        ],
                      };

                    return groupItem;
                  })
                : [],
            };

          return measure;
        });
      });
    } catch (error) {
      console.log(error);
    }

    if (isEditElementMode) setIsEditElementMode(undefined);
    if (isAddElementMode) setIsAddElementMode(false);
  }, [
    setMeasures,
    values?.building,
    values?.group,
    values?.name,
    values?.file,
    values?.surface_id,
    values?.comment,
    values?.units,
    stageUnitsList,
    stagesList,
    isAddElementMode,
    setIsAddElementMode,
    isEditElementMode,
    setIsEditElementMode,
  ]);

  const handleUploadFileChange = useCallback(
    (info: UploadChangeParam) => {
      setIsFileUploading(info.file.status === 'uploading');
    },
    [setIsFileUploading]
  );

  const handlePreview = useCallback(
    async (previewFile: UploadFile) => {
      setPreviewFile(previewFile);
    },
    [setPreviewFile]
  );

  const validateUnits = useCallback(async () => {
    const values = form.getFieldsValue();
    const units: { [key: string]: string } = values.units || {};

    const isNotEmpty = Object.values(units).find((unit) => {
      return unit ? !!unit.trim() : false;
    });

    if (!isNotEmpty)
      return Promise.reject(
        new Error('Заполните хотя бы одно поле Площадь / величина')
      );

    return Promise.resolve();
  }, [form]);

  return (
    <>
      <Flex vertical gap={16}>
        <Form
          layout={'vertical'}
          form={form}
          onFinish={handleFinish}
          initialValues={{
            name: '',
            file: null,
          }}
        >
          <Form.Item
            name="building"
            label="Помещение"
            rules={[
              {
                required: true,
                message: 'Пожалуйста, выберите помещение!',
              },
            ]}
            validateTrigger={false}
            required={false}
          >
            <Select placeholder="Выберите помещение">
              {measures
                .filter((measure) => !!measure.groups?.length)
                .map((measure, index) => (
                  <Select.Option key={index} value={measure.id.toString()}>
                    {measure.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="group"
            label="Группа"
            rules={[
              {
                required: true,
                message: 'Пожалуйста, выберите группу!',
              },
            ]}
            validateTrigger={false}
            required={false}
          >
            <Select placeholder="Выберите группу">
              {groupsList.map((group, index) => (
                <Select.Option key={index} value={group.id.toString()}>
                  {group.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="name"
            label="Наименование"
            rules={[
              {
                required: true,
                message: 'Пожалуйста, введите наименование!',
              },
            ]}
            validateTrigger={false}
            required={false}
          >
            <Input size="large" />
          </Form.Item>
          <Form.Item
            name="surface_id"
            label="Тип поверхности"
            rules={[
              {
                required: true,
                message: 'Пожалуйста, выберите тип поверхности!',
              },
            ]}
            validateTrigger={false}
            required={false}
          >
            <Select placeholder="Выберите тип поверхности">
              {surfacesList.data.map((surface, index) => {
                return (
                  <Select.Option key={index} value={surface.id}>
                    {surface.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          {aviableUnits.map((aviableUnit, index) => {
            return (
              <Form.Item
                name={['units', aviableUnit.id.toString()]}
                label={`Площадь / величина - ${aviableUnit.name}`}
                validateTrigger={false}
                required={false}
                rules={[
                  {
                    validator: validateUnits,
                  },
                ]}
                key={index}
              >
                <Input size="large" />
              </Form.Item>
            );
          })}
          <Form.Item
            label="Добавить фото или чертеж"
            name="file"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            <Upload
              action={`${apiDomain}/api/upload-file`}
              accept="image/*"
              maxCount={1}
              withCredentials={true}
              onChange={handleUploadFileChange}
              onPreview={handlePreview}
              listType="picture-card"
            >
              <Button icon={<UploadOutlined />} disabled={isFileUploading}>
                {isFileUploading ? `Идет загрузка` : `Загрузить файл`}
              </Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name="comment"
            label="Комментарий"
            validateTrigger={false}
            required={false}
          >
            <Input size="large" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              size="large"
              disabled={!isSubmittable || isFileUploading}
              htmlType="submit"
            >
              Сохранить
            </Button>
          </Form.Item>
        </Form>
      </Flex>
      {isMobile && (
        <MobileModal
          visible={!!previewFile?.url || !!previewFile?.thumbUrl}
          content={
            <img
              alt={previewFile?.fileName}
              style={{ width: '100%' }}
              src={
                previewFile?.url
                  ? previewFile?.url
                  : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  : previewFile?.thumbUrl
              }
              onClick={() => setPreviewFile(undefined)}
            />
          }
          closeOnMaskClick={true}
          showCloseButton={true}
          onClose={() => setPreviewFile(undefined)}
        />
      )}
      {!isMobile && (
        <Modal
          open={!!previewFile?.url || !!previewFile?.thumbUrl}
          title={previewFile?.fileName}
          footer={null}
          onCancel={() => setPreviewFile(undefined)}
        >
          <img
            alt={previewFile?.fileName}
            style={{ width: '100%' }}
            src={
              previewFile?.url
                ? previewFile?.url
                : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                : previewFile?.thumbUrl
            }
            onClick={() => setPreviewFile(undefined)}
          />
        </Modal>
      )}
    </>
  );
};

export default Component;
